/**
 * Amplitude (product analytics) — quatrième destination web, aux côtés de
 * PostHog, GA4 et du Meta Pixel. Pendant web de `services/analytics/amplitude.ts`
 * de l'app mobile : même projet, même convention d'événements, pour que les
 * parcours web et app se lisent ensemble.
 *
 * Pourquoi Amplitude EN PLUS de PostHog : les deux sont facturés à l'événement,
 * mais pas au même plafond. PostHog s'ARRÊTE à 1 M/mois (palier gratuit, quota
 * partagé avec le mobile qui en consomme déjà ~350 k) ; Amplitude dispose de
 * 2 M. Amplitude reçoit donc tout le plan de tracking, PostHog un sous-ensemble
 * strict (cf. `./tracking-plan`).
 *
 * Désactivé tant que `NEXT_PUBLIC_AMPLITUDE_API_KEY` n'est pas renseignée :
 * toutes les fonctions deviennent alors des no-op silencieux.
 *
 * @see https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2
 */
import * as amplitude from "@amplitude/analytics-browser";
import { Identify, Revenue } from "@amplitude/analytics-browser";

export const AMPLITUDE_API_KEY =
  process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY ?? "";

const FORCE_ENABLE = process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === "true";

/** Activé en production (ou en local si le flag debug est posé) et si une clé existe. */
export const AMPLITUDE_ENABLED =
  !!AMPLITUDE_API_KEY &&
  (process.env.NODE_ENV === "production" || FORCE_ENABLE);

/**
 * Clés de propriétés volumineuses (format e-commerce GA4) sans intérêt ici :
 * la liste complète des produits est destinée à Google et Meta pour
 * l'attribution. Amplitude, en plus, impose une taille maximale par événement.
 * Même liste que `./posthog`.
 */
const HEAVY_PROP_KEYS = ["items", "products", "menu", "categories", "cart_items"];

function stripHeavyProps(
  properties: Record<string, unknown>
): Record<string, unknown> {
  let cleaned: Record<string, unknown> | undefined;
  for (const key of Object.keys(properties)) {
    const value = properties[key];
    const isHeavy =
      HEAVY_PROP_KEYS.includes(key) ||
      (Array.isArray(value) &&
        value.some((v) => v !== null && typeof v === "object"));
    if (isHeavy) {
      if (!cleaned) cleaned = { ...properties };
      delete cleaned[key];
    }
  }
  return cleaned ?? properties;
}

let _initPromise: Promise<void> | null = null;
let _ready = false;

/**
 * Exécute `fn` une fois le SDK prêt.
 *
 * ⚠️ Contrairement à PostHog, `amplitude.init()` est **asynchrone**. Tant que
 * sa promesse n'est pas résolue, aucun plugin de destination n'est attaché et
 * le SDK **jette les événements**, en se contentant d'un
 * « Event not tracked, no destination plugins on the instance » en console.
 * C'est le même piège que côté mobile, où l'init est attendue avant tout track.
 */
function whenReady(fn: () => void): void {
  if (!AMPLITUDE_ENABLED) return;
  if (_ready) fn();
  else void _initPromise?.then(fn);
}

/** Initialise Amplitude côté navigateur. Idempotent et silencieux si désactivé. */
export function initAmplitude(userId?: string): void {
  if (_initPromise || typeof window === "undefined" || !AMPLITUDE_ENABLED)
    return;

  _initPromise = amplitude
    .init(AMPLITUDE_API_KEY, userId, {
      // Projet hébergé dans l'UE : sans ce serverZone les événements partent
      // sur l'ingest US et n'arrivent JAMAIS dans le projet. Piège n°1.
      serverZone: "EU",

      // La configuration distante peut RÉACTIVER l'autocapture depuis la
      // console Amplitude, sans passer par le code. On la coupe pour que ce
      // fichier reste la seule source de vérité de ce qui est collecté — donc
      // facturé.
      remoteConfig: { fetchRemoteConfig: false },

      // Autocapture posé EXPLICITEMENT, option par option : plusieurs défauts
      // du SDK sont à `true` et émettent en continu.
      autocapture: {
        // Vues de page, au chargement ET sur navigation SPA — le SDK écoute
        // l'historique lui-même, `analytics.trackPage` ne doit donc PAS lui
        // en renvoyer (sinon chaque vue compte double).
        // `pathOnly` : un changement de query string (filtres, tri) n'est pas
        // une nouvelle page.
        pageViews: { trackHistoryChanges: "pathOnly" },

        // ~2 événements par session, base de tout découpage temporel.
        sessions: true,

        // Attribution des campagnes : renseigne des propriétés UTILISATEUR,
        // n'émet pas d'événement (`fallbackAttributionEvent` est à false).
        attribution: true,

        // ── Défauts du SDK à `true` qu'on coupe ──────────────────────────
        formInteractions: false, // un événement par interaction de formulaire
        fileDownloads: false, // sans objet sur ce site

        // ── Déjà à `false`, posés pour que le choix soit lisible ─────────
        elementInteractions: false, // un événement par clic
        networkTracking: false, // un événement par requête HTTP
        frustrationInteractions: false,
        webVitals: false,
        performanceTracking: false,
      },
    })
    .promise.then(() => {
      _ready = true;
      // Pendant de la super-propriété `platform: web` de PostHog : Amplitude
      // n'a pas de super properties, on passe par une propriété utilisateur.
      const identify = new Identify();
      identify.set("platform", "web");
      amplitude.identify(identify);
    });
}

/**
 * Identifier l'utilisateur (login/signup). Même clé d'identité que PostHog et
 * que le mobile — **le téléphone** — pour que les trois parlent du même
 * utilisateur ; repli sur l'id si le téléphone manque.
 */
export function identifyAmplitudeUser(user: {
  id?: string;
  phone?: string;
  email?: string;
  name?: string;
}): void {
  whenReady(() => {
    const userId = user.phone ?? user.id;
    if (userId) amplitude.setUserId(userId);

    const identify = new Identify();
    if (user.email) identify.set("email", user.email);
    if (user.name) identify.set("name", user.name);
    if (user.phone) identify.set("phone", user.phone);
    amplitude.identify(identify);
  });
}

/** Logout — régénère un identifiant anonyme, sans continuité avec la session. */
export function amplitudeReset(): void {
  whenReady(() => amplitude.reset());
}

/** Événement métier (nom déjà traduit en Object-Action par la façade). */
export function trackAmplitudeEvent(
  event: string,
  properties?: Record<string, unknown>
): void {
  whenReady(() =>
    amplitude.track(event, properties ? stripHeavyProps(properties) : undefined)
  );
}

/**
 * Achat. Passe par l'objet `Revenue` et non par un événement custom : c'est ce
 * qui alimente les rapports de revenu d'Amplitude (LTV, ARPU). Un
 * `track("purchase", { amount })` n'y apparaîtrait pas.
 */
export function trackAmplitudePurchase(
  amount: number,
  currency: string,
  properties?: Record<string, unknown>
): void {
  whenReady(() => {
    const revenue = new Revenue()
      .setPrice(amount)
      .setQuantity(1)
      .setCurrency(currency)
      .setRevenueType("purchase");

    const cleaned = properties ? stripHeavyProps(properties) : undefined;
    if (cleaned) {
      revenue.setEventProperties(
        cleaned as Parameters<Revenue["setEventProperties"]>[0]
      );
    }
    const productId = properties?.transaction_id ?? properties?.order_id;
    if (productId) revenue.setProductId(String(productId));

    amplitude.revenue(revenue);

    // Un objet Revenue alimente les rapports de revenu mais n'apparaît PAS
    // dans la liste d'événements : impossible de bâtir un funnel qui s'y
    // termine. D'où l'événement nommé en plus, avec les mêmes propriétés.
    amplitude.track("Order Completed", cleaned);
  });
}

/**
 * Propriétés persistantes. Pendant des super properties PostHog : Amplitude
 * n'a pas d'équivalent exact et les enregistre comme propriétés UTILISATEUR —
 * même intention, granularité différente.
 */
export function setAmplitudeUserProperties(
  properties: Record<string, unknown>
): void {
  whenReady(() => {
    const identify = new Identify();
    for (const [key, value] of Object.entries(properties)) {
      if (value !== undefined && value !== null) {
        identify.set(key, value as string | number | boolean);
      }
    }
    amplitude.identify(identify);
  });
}
